/**
 * Supabase browser client (typed singleton).
 *
 * The client owns its own auth session: it is created with `persistSession`,
 * `autoRefreshToken`, and the PKCE flow. This is the recommended model — we do
 * NOT store raw JWTs ourselves. The shared auth bridge reads the current access
 * token from the live SDK session (see {@link getSupabaseAccessToken}).
 *
 * Session PERSISTENCE is switchable at runtime: the client is created with a
 * custom storage adapter whose backing store („Запомни ме" on / off) can be
 * flipped through {@link setSessionPersistence} without recreating the client
 * — recreating it would drop live realtime channels and the in-flight PKCE
 * verifier.
 *
 * The client is created LAZILY (on first access); importing this module has
 * no side effects until {@link getSupabaseClient} or {@link getAuthStorage} is
 * called.
 *
 * @module shared/clients/supabaseClient
 */

import {
  createClient,
  type AuthChangeEvent,
  type Session,
  type SupabaseClient,
} from '@supabase/supabase-js';
import { env } from '@/shared/lib/env';
import type { Database } from '@/shared/types/database';

/** Strongly-typed Supabase client bound to our generated {@link Database}. */
export type TypedSupabaseClient = SupabaseClient<Database>;

let client: TypedSupabaseClient | null = null;

/**
 * Where the session lives.
 *
 * - `local`   — `localStorage`, survives a browser restart („Запомни ме" on)
 * - `session` — `sessionStorage`, dies with the browser session („Запомни ме"
 *               off). This app runs on a SHARED FAMILY DEVICE, so an
 *               unchecked box must really mean "do not remember me".
 */
export type PersistenceMode = 'local' | 'session';

/**
 * Storage surface handed to the Supabase SDK (`auth.storage`) and to the
 * zustand `persist` middleware, plus the runtime switch both share.
 */
export interface SwitchableStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly removeItem: (key: string) => void;
  /**
   * Flip the active backing store, migrating existing keys across — except
   * the PKCE code verifier, which always stays in `localStorage` (see
   * {@link PKCE_VERIFIER_KEY_PATTERN}).
   */
  readonly setMode: (mode: PersistenceMode) => void;
  /** Currently active backing store. */
  readonly getMode: () => PersistenceMode;
}

/** Supabase writes its session under `sb-<project-ref>-auth-token…`. */
const SUPABASE_AUTH_KEY_PATTERN = /^sb-.+-auth-token/;

/**
 * The PKCE code verifier, which the SDK parks next to the session under
 * `<storageKey>-code-verifier` (auth-js `getCodeChallengeAndMethod`) — so it
 * also matches {@link SUPABASE_AUTH_KEY_PATTERN}.
 *
 * It is EXEMPT from persistence switching: it is written to BOTH Web Storage
 * areas on purpose, whatever „Запомни ме" says, and `setMode` never migrates
 * it. `localStorage` is what the recovery e-mail needs: the link opens in a
 * NEW TAB, and `sessionStorage` is per tab, so a verifier parked only there
 * is invisible to the tab that has to redeem it. auth-js then finds nothing,
 * treats the callback as "not a PKCE callback", skips the exchange WITHOUT
 * raising, and the user is told a perfectly good link is invalid.
 * `sessionStorage` is the degradation path: when `localStorage` is blocked
 * (Safari private mode, "block site data" policies) it collapses to an
 * in-memory store that dies on the full-page navigation, which would break
 * even the same-tab case — pasting the link into the tab that requested the
 * reset. Reads prefer `localStorage`; `removeItem` already clears both, so
 * the second copy leaves nothing behind.
 *
 * This does not weaken the „Запомни ме" promise: the verifier is not a
 * session but a short-lived, single-use secret for completing one flow, and
 * the SDK deletes it as soon as the code is exchanged. Do not "tidy" this
 * exemption away.
 */
const PKCE_VERIFIER_KEY_PATTERN = /-code-verifier$/;

/** Whether a key holds the PKCE verifier (see {@link PKCE_VERIFIER_KEY_PATTERN}). */
function isPkceVerifierKey(key: string): boolean {
  return PKCE_VERIFIER_KEY_PATTERN.test(key);
}

/** Normalised key/value access over one backing store. */
interface Backing {
  readonly get: (key: string) => string | null;
  readonly set: (key: string, value: string) => void;
  readonly remove: (key: string) => void;
  readonly keys: () => readonly string[];
}

/** In-memory backing store — used when Web Storage is absent or throws. */
function createMemoryBacking(): Backing {
  const entries = new Map<string, string>();

  return {
    get: (key) => entries.get(key) ?? null,
    set: (key, value) => {
      entries.set(key, value);
    },
    remove: (key) => {
      entries.delete(key);
    },
    keys: () => [...entries.keys()],
  };
}

/**
 * Wrap a Web Storage object so no access can throw: SSR / jsdom have no
 * `window`, Safari private mode and disabled-storage policies throw on read,
 * and a full quota throws on write. Anything that fails degrades to an
 * in-memory store for the lifetime of the page.
 */
function createBacking(storage: Storage | null): Backing {
  if (!storage) {
    return createMemoryBacking();
  }

  const fallback = createMemoryBacking();

  return {
    get: (key) => {
      try {
        const value = storage.getItem(key);
        if (value !== null) {
          return value;
        }
      } catch {
        /* storage unreadable — fall through to the memory copy */
      }
      return fallback.get(key);
    },
    set: (key, value) => {
      try {
        storage.setItem(key, value);
        fallback.remove(key);
      } catch {
        // QuotaExceededError / disabled storage: keep the value in memory so
        // the session still works for this page load. The real store may
        // still hold the PREVIOUS value, and `get` prefers it — drop it, or a
        // stale pre-rotation session would win the next read and sign the
        // user out mid-use.
        try {
          storage.removeItem(key);
        } catch {
          /* unreadable store — the memory copy is all we have anyway */
        }
        fallback.set(key, value);
      }
    },
    remove: (key) => {
      try {
        storage.removeItem(key);
      } catch {
        /* nothing to remove */
      }
      fallback.remove(key);
    },
    keys: () => {
      // Keys that only made it into the memory copy (a failed write) count
      // too — they are live values for this page load.
      const found = new Set<string>(fallback.keys());
      try {
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key !== null) {
            found.add(key);
          }
        }
      } catch {
        /* store unreadable — the memory copy is all we can enumerate */
      }
      return [...found];
    },
  };
}

/**
 * Build a storage adapter that reads from both Web Storage areas but writes
 * to exactly one of them, switchable at runtime.
 *
 * Exported for tests — production code uses the {@link getAuthStorage}
 * singleton.
 *
 * @param localStorageRef - `window.localStorage`, or `null` when unavailable
 * @param sessionStorageRef - `window.sessionStorage`, or `null`
 */
export function createSwitchableStorage(
  localStorageRef: Storage | null,
  sessionStorageRef: Storage | null
): SwitchableStorage {
  const backings: Readonly<Record<PersistenceMode, Backing>> = {
    local: createBacking(localStorageRef),
    session: createBacking(sessionStorageRef),
  };

  // Every key this adapter has touched, so a mode flip can migrate them.
  const managedKeys = new Set<string>();

  // Adopt `session` when a previous "do not remember me" login left its token
  // there: otherwise the first autoRefresh write after a reload would land in
  // localStorage and the session would outlive the browser session after all.
  // A stray code verifier is not a session and never proves anything about
  // the mode, so it is ignored here as everywhere else.
  let mode: PersistenceMode = backings.session
    .keys()
    .some(
      (key) =>
        SUPABASE_AUTH_KEY_PATTERN.test(key) &&
        !isPkceVerifierKey(key) &&
        backings.local.get(key) === null
    )
    ? 'session'
    : 'local';

  const inactive = (): Backing =>
    mode === 'local' ? backings.session : backings.local;

  return {
    getItem: (key) => {
      if (isPkceVerifierKey(key)) {
        // localStorage is the primary copy (it is the one a NEW TAB can see);
        // sessionStorage is the fallback for when localStorage is blocked and
        // its in-memory stand-in did not survive the page load.
        return backings.local.get(key) ?? backings.session.get(key);
      }
      managedKeys.add(key);
      // The ACTIVE store wins, which is the same store the boot-time mode
      // detection above trusted. Reading the other one first could promote a
      // "do not remember me" session to permanent on the next write.
      return backings[mode].get(key) ?? inactive().get(key);
    },
    setItem: (key, value) => {
      if (isPkceVerifierKey(key)) {
        // Both stores, deliberately — see PKCE_VERIFIER_KEY_PATTERN. Neither
        // area alone covers both the new-tab and the blocked-localStorage
        // cases.
        backings.local.set(key, value);
        backings.session.set(key, value);
        return;
      }
      managedKeys.add(key);
      // Write to the active store only, and make sure no stale copy survives
      // in the other one (that copy is what "remembers" an unchecked box).
      inactive().remove(key);
      backings[mode].set(key, value);
    },
    removeItem: (key) => {
      managedKeys.delete(key);
      backings.local.remove(key);
      backings.session.remove(key);
    },
    setMode: (next) => {
      if (next === mode) {
        return;
      }

      const from = backings[mode];
      const to = backings[next];
      const keysToMigrate = new Set<string>(
        [
          ...managedKeys,
          ...from.keys().filter((key) => SUPABASE_AUTH_KEY_PATTERN.test(key)),
        ].filter((key) => !isPkceVerifierKey(key))
      );

      mode = next;

      for (const key of keysToMigrate) {
        const value = from.get(key);
        if (value !== null) {
          to.set(key, value);
          from.remove(key);
        }
      }
    },
    getMode: () => mode,
  };
}

/**
 * Resolve one Web Storage area, or `null` when the environment has none
 * (SSR, or a browser that throws on access because storage is blocked).
 */
function resolveStorage(
  area: 'localStorage' | 'sessionStorage'
): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    return window[area];
  } catch {
    return null;
  }
}

/** Lazily created so importing this module stays side-effect free. */
let authStorage: SwitchableStorage | null = null;

/**
 * The shared auth storage adapter. Used by the Supabase client for its
 * session AND by the auth store's zustand `persist` entry, so a single
 * „Запомни ме" choice governs both.
 */
export function getAuthStorage(): SwitchableStorage {
  authStorage ??= createSwitchableStorage(
    resolveStorage('localStorage'),
    resolveStorage('sessionStorage')
  );
  return authStorage;
}

/**
 * Honour the „Запомни ме" choice.
 *
 * `true` keeps the session in `localStorage` (survives a browser restart);
 * `false` moves it to `sessionStorage`, so closing the browser really does
 * forget the user. Existing keys migrate immediately, so calling this right
 * before `signInWithPassword` is enough — and no client recreation happens,
 * which keeps realtime channels and the PKCE verifier intact.
 */
export function setSessionPersistence(remember: boolean): void {
  getAuthStorage().setMode(remember ? 'local' : 'session');
}

/**
 * In-memory cache of the current session tokens, kept in sync via
 * `onAuthStateChange`. The auth bridge reads these synchronously.
 */
let cachedAccessToken: string | null = null;
let cachedRefreshToken: string | null = null;

/**
 * Storage key for the password-recovery marker: the id of the user whose
 * session was established by a recovery link.
 *
 * It is PERSISTED, through the same switchable adapter as the session, so it
 * lives in whichever store the session lives in and dies exactly when that
 * store does. A module-level flag would be lost on reload — and the marker
 * cannot be rebuilt from the event stream, because auth-js emits `SIGNED_IN`
 * (not `PASSWORD_RECOVERY`) when it restores a session from storage, and
 * `INITIAL_SESSION` only to newly-registered subscribers. Without the marker
 * `/reset-password` would send a reloading user to `/change-password`, which
 * asks for the current password they came here precisely because they do not
 * know.
 *
 * The value is the recovering user's id so the marker cannot leak onto a
 * different identity signing in later on this shared family device.
 */
const RECOVERY_MARKER_KEY = 'auth-recovery-user';

/**
 * Marker value used when `PASSWORD_RECOVERY` arrives without a session, so
 * the marker still gates the page but binds to no identity. auth-js always
 * attaches the recovery session in practice; this is the type-level tail.
 */
const UNBOUND_RECOVERY_USER = '';

/**
 * Lazily create (or return) the typed Supabase singleton.
 *
 * @throws Error when the Supabase env vars are missing — a misconfigured
 *   build (env validation should normally reject it earlier).
 */
export function getSupabaseClient(): TypedSupabaseClient {
  if (client) {
    return client;
  }

  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      'Supabase клиентът не може да бъде инициализиран: липсват VITE_SUPABASE_URL и/или VITE_SUPABASE_ANON_KEY. ' +
        'Тези променливи са задължителни.'
    );
  }

  client = createClient<Database>(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      // Switchable backing store — see setSessionPersistence().
      storage: getAuthStorage(),
    },
  });

  // Keep the token cache warm for the synchronous auth bridge.
  client.auth
    .getSession()
    .then(({ data }) => {
      cachedAccessToken = data.session?.access_token ?? null;
      cachedRefreshToken = data.session?.refresh_token ?? null;
    })
    .catch(() => {
      /* no active session */
    });

  client.auth.onAuthStateChange((event, session) => {
    cachedAccessToken = session?.access_token ?? null;
    cachedRefreshToken = session?.refresh_token ?? null;

    if (event === 'PASSWORD_RECOVERY') {
      // Pin the recovery session to THIS TAB before marking it. A recovery
      // session is never something to "remember": the link boots a tab with
      // an empty sessionStorage, so the mode detector would otherwise resolve
      // to `local` and auth-js would park a live session plus its refresh
      // token in localStorage. A user who opens the link and closes the tab
      // without submitting — distraction, second thoughts, a validation error
      // — would leave that session behind a browser restart, and the next
      // person on this shared family device would be silently signed in as
      // them.
      //
      // Accepted cost: someone who had „Запомни ме" checked and then abandons
      // a recovery in the same browser has their existing session demoted to
      // session scope, so it no longer survives a browser restart. That is
      // the safe direction and is deliberate.
      //
      // The migration carries the just-written session across; the PKCE
      // verifier is exempt from it, so the in-flight exchange is unaffected.
      const storage = getAuthStorage();
      storage.setMode('session');
      storage.setItem(
        RECOVERY_MARKER_KEY,
        session?.user.id ?? UNBOUND_RECOVERY_USER
      );
      return;
    }

    if (event === 'SIGNED_OUT' || session === null) {
      // A recovery session that merely refreshes (`TOKEN_REFRESHED`) or whose
      // user is updated is still a recovery session — only losing the session
      // clears the marker.
      clearRecoverySession();
      return;
    }

    // NEVER clear the marker on `SIGNED_IN`: that is the event auth-js emits
    // when it restores a persisted session on RELOAD, so clearing here would
    // re-create the dead end the persisted marker exists to fix. Only a
    // genuinely DIFFERENT identity drops it.
    const marked = getAuthStorage().getItem(RECOVERY_MARKER_KEY);
    if (
      marked !== null &&
      marked !== UNBOUND_RECOVERY_USER &&
      marked !== session.user.id
    ) {
      clearRecoverySession();
    }
  });

  return client;
}

/**
 * True when the current session was established by a password-recovery link.
 *
 * Reads the persisted marker straight from the storage adapter, so it stays
 * synchronous and answers correctly after a reload — the marker outlives the
 * page, not just the module instance.
 *
 * Still read this only AFTER awaiting `auth.getSession()`: on the FIRST leg
 * of the flow the marker is written by the `PASSWORD_RECOVERY` handler during
 * the SDK's initialisation, so a read before it can still see `false`.
 */
export function isRecoverySession(): boolean {
  return getAuthStorage().getItem(RECOVERY_MARKER_KEY) !== null;
}

/** Forget the recovery marker (after the password has been changed). */
export function clearRecoverySession(): void {
  getAuthStorage().removeItem(RECOVERY_MARKER_KEY);
}

/**
 * Current access token from the live Supabase session, or `null` when no
 * session is cached yet — before {@link getSupabaseClient} has been called, or
 * while signed out.
 */
export function getSupabaseAccessToken(): string | null {
  return cachedAccessToken;
}

/**
 * Current refresh token from the live Supabase session, or `null`.
 */
export function getSupabaseRefreshToken(): string | null {
  return cachedRefreshToken;
}

/**
 * Subscribe to Supabase auth state changes. Used by the auth store to mirror
 * sign-in / sign-out / token-refresh into app state. Returns an unsubscribe
 * function.
 */
export function onSupabaseAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
): () => void {
  const {
    data: { subscription },
  } = getSupabaseClient().auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
  return () => subscription.unsubscribe();
}

/**
 * How long {@link disconnectAllRealtime} waits for the channels to actually
 * close before it stops holding its callers up.
 *
 * `removeAllChannels()` sends a `phx_leave` on every channel and awaits the
 * server's reply for each, bounded only by realtime-js's own 10 s push
 * timeout, and only then disconnects the socket. Its callers await this
 * teardown for ORDERING — a family rescope re-advertises the new scope the
 * moment the teardown returns, and sign-out must not resolve while a channel
 * from the previous session is still open — so an unreachable server must not
 * be able to stall a family switch or a sign-out for ten seconds.
 *
 * Past the bound the teardown is left to finish on its own. The local channel
 * objects are dropped either way, so the worst case is a server-side
 * subscription the server reaps on its own heartbeat timeout (~60 s), which is
 * exactly what an ordinary tab close leaves behind.
 */
const REALTIME_TEARDOWN_TIMEOUT_MS = 2_000;

/**
 * Tear down every active realtime channel.
 *
 * Called on logout and on every family rescope so no websocket subscription
 * survives a scope change (which would otherwise leak the previous user's or
 * the previous family's channels into the next one). Safe to call before the
 * client exists — it is a no-op when no client has been created.
 *
 * AWAIT IT. The returned promise is what orders the teardown against whatever
 * re-subscribes next; it is bounded by {@link REALTIME_TEARDOWN_TIMEOUT_MS}
 * and never rejects, so awaiting it cannot hang or fail a sign-out.
 */
export async function disconnectAllRealtime(): Promise<void> {
  if (!client) {
    return;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, REALTIME_TEARDOWN_TIMEOUT_MS);
  });

  try {
    await Promise.race([
      client.removeAllChannels().then(
        () => undefined,
        () => undefined
      ),
      bound,
    ]);
  } finally {
    clearTimeout(timer);
  }
}
